<template>
  <div class="app-container">
    <!-- 头部导航 -->
    <header>
      <div class="container">
        <div class="header-wrap">
          <div class="logo">
            <span class="sigma-tag">Sigma</span>
            <span>中安培交通科技福州鼓楼有限公司</span>
          </div>
          <nav>
            <ul :class="{ active: menuOpen }" id="nav-list">
              <li v-for="item in navItems" :key="item.id">
                <router-link 
                  v-if="item.path"
                  :to="item.path"
                  :class="{ active: activeLink === item.id }"
                >{{ item.name }}</router-link>
                <a 
                  v-else
                  :href="'#' + item.id" 
                  :class="{ active: activeLink === item.id }"
                  @click.prevent="scrollToSection(item.id)"
                >{{ item.name }}</a>
              </li>
            </ul>
          </nav>
          <div class="mobile-menu" @click="toggleMenu">{{ menuOpen ? '✕' : '☰' }}</div>
        </div>
      </div>
    </header>

    <!-- 首屏 Banner -->
    <section class="banner" id="home">
      <div class="container">
        <div class="banner-content">
          <h1>中安培交通科技福州鼓楼有限公司</h1>
          <p>福州鼓楼区正规合规驾培机构 | 专业教练团队 | 标准化训练场 | 一站式驾考服务</p>
          <div class="banner-btn">
            <a href="#service" @click.prevent="scrollToSection('service')" class="btn-primary">查看服务</a>
            <a href="#contact" @click.prevent="scrollToSection('contact')" class="btn-outline">立即报名</a>
          </div>
        </div>
      </div>
    </section>

    <!-- 企业介绍模块 -->
    <section class="section" id="about">
      <div class="container">
        <div class="breadcrumb">
          <a href="/">首页</a> > 企业介绍
        </div>
        <div class="section-title">
          <h2>{{ aboutData?.title || '企业介绍' }}</h2>
          <p>{{ aboutData?.subtitle || '深耕福州鼓楼驾培行业，合规持证经营，专注为学员提供安全、专业、高效的驾驶培训服务' }}</p>
        </div>
        <div class="about-wrap" v-if="aboutData">
          <div class="about-img">
            <img :src="getImageUrl(aboutData.image)" :alt="aboutData.contentTitle || '企业介绍'" loading="lazy">
          </div>
          <div class="about-content">
            <h3>{{ aboutData.contentTitle || '福州鼓楼本土专业驾培服务商' }}</h3>
            <p v-for="(para, idx) in aboutData.paragraphs" :key="idx">{{ para }}</p>
            <div class="about-data">
              <div v-for="(stat, idx) in aboutData.stats" :key="idx" class="data-item">
                <span class="data-num">{{ stat.number }}</span>
                <span class="data-label">{{ stat.label }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 服务介绍模块 -->
    <section class="section" id="service">
      <div class="container">
        <div class="breadcrumb">
          <a href="/">首页</a> > 服务介绍
        </div>
        <div class="section-title">
          <h2>服务介绍</h2>
          <p>全品类驾培服务覆盖，满足不同学员的多样化学车需求，一站式解决所有驾考难题</p>
        </div>
        <div class="service-wrap" v-if="serviceList.length">
          <div v-for="item in serviceList" :key="item.id" class="service-card">
            <img :src="getImageUrl(item.image)" :alt="item.title" class="service-img" loading="lazy">
            <div class="service-content">
              <h3>{{ item.title }}</h3>
              <div class="service-tags">
                <span v-for="tag in item.tags" :key="tag" class="service-tag">{{ tag }}</span>
              </div>
              <p>{{ item.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 客户案例模块 -->
    <section class="section" id="case">
      <div class="container">
        <div class="breadcrumb">
          <a href="/">首页</a> > 客户案例
        </div>
        <div class="section-title">
          <h2>客户案例</h2>
          <p>万余名学员的共同选择，真实培训案例见证中安培的教学实力与服务品质</p>
        </div>
        <ul class="case-filter">
          <li :class="{ active: activeCaseFilter === 'all' }" @click="activeCaseFilter = 'all'">全部案例</li>
          <li :class="{ active: activeCaseFilter === 'student' }" @click="activeCaseFilter = 'student'">学员拿证案例</li>
          <li :class="{ active: activeCaseFilter === 'enterprise' }" @click="activeCaseFilter = 'enterprise'">企业合作案例</li>
        </ul>
        <div class="case-wrap">
          <div v-for="(item, idx) in filteredCaseList" :key="idx" class="case-card">
            <img :src="item.imgUrl" :alt="item.title" class="case-img" loading="lazy">
            <div class="case-content">
              <div class="case-tags">
                <span v-for="tag in getCaseTags(item.tag)" :key="tag" class="case-tag">{{ tag }}</span>
              </div>
              <h3>{{ item.title }}</h3>
              <p class="case-desc">{{ item.desc }}</p>
              <div class="case-detail" v-if="item.desc.length > 50">{{ item.desc }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 常见问题模块 -->
    <section class="section" id="faq">
      <div class="container">
        <div class="breadcrumb">
          <a href="/">首页</a> > 常见问题
        </div>
        <div class="section-title">
          <h2>常见问题</h2>
          <p>解答学员学车过程中最关心的问题，一站式消除您的学车顾虑</p>
        </div>
        <div class="faq-wrap">
          <div v-for="(item, idx) in faqList" :key="idx" class="faq-item" :class="{ active: activeFaq === idx }">
            <div class="faq-question" @click="toggleFaq(idx)">{{ item.question }}</div>
            <div class="faq-answer">
              <p v-html="formatAnswer(item.answer)"></p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 荣誉资质模块 -->
    <section class="section" id="honor">
      <div class="container">
        <div class="breadcrumb">
          <a href="/">首页</a> > 荣誉资质
        </div>
        <div class="section-title">
          <h2>荣誉资质</h2>
          <p>合规经营是我们的立身之本，所有资质均可官方核验，权威认证见证品牌实力</p>
        </div>
        <div class="honor-wrap">
          <div v-for="(item, idx) in honorList" :key="idx" class="honor-card">
            <div class="honor-img-wrap">
              <img :src="item.imgUrl" :alt="item.name" loading="lazy">
            </div>
            <div class="honor-card-content">
              <h3>{{ item.name }}</h3>
              <span class="honor-type">{{ getHonorType(item.name) }}</span>
              <p class="honor-card-desc" v-if="item.description">{{ item.description }}</p>
              <p class="honor-code" v-if="item.certNumber"><strong>证书编号：</strong>{{ item.certNumber }}</p>
              <p class="honor-check" v-if="item.verifyUrl || item.verifyMethod"><strong>核验渠道：</strong>{{ item.verifyMethod || '可通过相关主管部门官网查询' }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 联系我们模块 -->
    <section class="section" id="contact">
      <div class="container">
        <div class="breadcrumb">
          <a href="/">首页</a> > 联系我们
        </div>
        <div class="section-title">
          <h2>联系我们</h2>
          <p>随时欢迎您的咨询与到访，中安培驾培竭诚为您提供专业的驾培服务</p>
        </div>
        <div class="contact-wrap">
          <div class="contact-info">
            <h3>联系我们</h3>
            <div class="contact-item">
              <h4>公司全称</h4>
              <p>中安培交通科技福州鼓楼有限公司</p>
            </div>
            <div class="contact-item">
              <h4>总部地址</h4>
              <p>福建省福州市鼓楼区XX街道XX路XX号XX大厦1层101-105单元</p>
            </div>
            <div class="contact-item">
              <h4>报名热线</h4>
              <p>0591-XXXXXXX | 13XXXXXXXXX（微信同号）</p>
            </div>
            <div class="contact-item">
              <h4>营业时间</h4>
              <p>周一至周日 08:00-20:00 全年无休</p>
            </div>
            <div class="contact-item">
              <h4>官方微信</h4>
              <p>公众号：福州中安培驾培</p>
            </div>
            <div class="contact-item">
              <h4>电子邮箱</h4>
              <p>zapjiaxiao@163.com</p>
            </div>
          </div>
          <div class="contact-form">
            <h3>在线报名咨询</h3>
            <form @submit.prevent="handleSubmit">
              <div class="form-group">
                <label for="name">您的姓名</label>
                <input type="text" id="name" placeholder="请输入您的姓名" required v-model="formData.name">
              </div>
              <div class="form-group">
                <label for="phone">联系电话</label>
                <input type="tel" id="phone" placeholder="请输入您的手机号码" required v-model="formData.phone">
              </div>
              <div class="form-group">
                <label for="type">意向班型</label>
                <input type="text" id="type" placeholder="请输入您的意向" v-model="formData.courseType">
              </div>
              <div class="form-group">
                <label for="content">咨询内容</label>
                <textarea id="content" placeholder="请输入您想咨询的问题，我们将尽快与您联系" v-model="formData.message"></textarea>
              </div>
              <button type="submit" class="submit-btn">提交咨询</button>
            </form>
          </div>
        </div>
      </div>
    </section>

    <!-- 页脚 -->
    <footer>
      <div class="container">
        <div class="footer-wrap">
          <div class="footer-col">
            <div class="footer-logo">
              <span class="sigma-tag">Sigma</span>
              <span>中安培交通科技福州鼓楼有限公司</span>
            </div>
            <p class="footer-desc">福州鼓楼区正规合规驾培机构，专注C1/C2驾驶证培训、摩托车驾考、满分审验教育、企业团驾服务，诚信办学，专业教学，服务至上，为您的驾驶之路保驾护航。</p>
          </div>
          <div class="footer-nav">
            <h4 class="footer-title">网站导航</h4>
            <ul>
              <li><a href="#about" @click.prevent="scrollToSection('about')">企业介绍</a></li>
              <li><a href="#case" @click.prevent="scrollToSection('case')">客户案例</a></li>
              <li><a href="#faq" @click.prevent="scrollToSection('faq')">常见问题</a></li>
              <li><a href="#honor" @click.prevent="scrollToSection('honor')">荣誉资质</a></li>
              <li><a href="#contact" @click.prevent="scrollToSection('contact')">联系我们</a></li>
            </ul>
          </div>
          <div class="footer-nav">
            <h4 class="footer-title">服务项目</h4>
            <ul>
              <li><a href="#case" @click.prevent="scrollToSection('case')">C1手动挡培训</a></li>
              <li><a href="#case" @click.prevent="scrollToSection('case')">C2自动挡培训</a></li>
              <li><a href="#case" @click.prevent="scrollToSection('case')">摩托车驾驶证培训</a></li>
              <li><a href="#faq" @click.prevent="scrollToSection('faq')">满分审验教育</a></li>
              <li><a href="#case" @click.prevent="scrollToSection('case')">企业团驾服务</a></li>
              <li><a href="#case" @click.prevent="scrollToSection('case')">新手陪练复训</a></li>
            </ul>
          </div>
          <div class="footer-nav">
            <h4 class="footer-title">联系我们</h4>
            <ul>
              <li>报名热线：0591-XXXXXXX</li>
              <li>咨询微信：13XXXXXXXXX</li>
              <li>地址：福州市鼓楼区XX路XX号</li>
              <li>营业时间：08:00-20:00</li>
            </ul>
          </div>
        </div>
        <div class="footer-copyright">
          © 2026 中安培交通科技福州鼓楼有限公司 版权所有 | <a href="https://beian.miit.gov.cn/">闽ICP备2026016637号</a> | 
          <span class="sigma-tag">Sigma</span>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import * as api from '@/api'

export default {
  name: 'App',
  data() {
    return {
      menuOpen: false,
      activeLink: 'home',
      activeFaq: null,
      activeCaseFilter: 'all',
      loading: true,
      formData: {
        name: '',
        phone: '',
        courseType: '',
        message: ''
      },
      navItems: [
        { id: 'about', name: '企业介绍' },
        { id: 'service', name: '服务介绍' },
        { id: 'case', name: '客户案例' },
        { id: 'faq', name: '常见问题' },
        { id: 'honor', name: '荣誉资质' },
        { id: 'contact', name: '联系我们' }
      ],
      aboutData: null,
      serviceList: [],
      caseList: [],
      faqList: [],
      honorList: []
    }
  },
  computed: {
    filteredCaseList() {
      if (this.activeCaseFilter === 'all') {
        return this.caseList
      }
      const enterpriseTags = ['企业', '团驾', '合作', '团培', '批量']
      return this.caseList.filter(item => {
        if (this.activeCaseFilter === 'enterprise') {
          return enterpriseTags.some(tag => item.tag && item.tag.includes(tag))
        }
        return true
      })
    }
  },
  async mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll();
    await this.fetchData();
    // 默认展开第一个FAQ
    if (this.faqList.length > 0) {
      this.activeFaq = 0
    }
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    getImageUrl(path) {
      if (!path) return ''
      // 如果是完整URL，直接返回
      if (path.startsWith('http')) return path
      // 如果是 /server/ 开头，转换为完整URL
      if (path.startsWith('/server/')) {
        return path.replace('/server', '')
      }
      // 如果是 /uploads/ 开头，加上服务器地址
      if (path.startsWith('/uploads/')) {
        return path
      }
      return path
    },
    async fetchData() {
      this.loading = true;
      try {
        const [aboutData, serviceList, caseList, faqList, honorList] = await Promise.all([
          api.getAbout(),
          api.getServices(),
          api.getCaseList(),
          api.getFaqList(),
          api.getHonorList()
        ]);
        this.aboutData = aboutData;
        this.serviceList = Array.isArray(serviceList) ? serviceList : [];
        this.caseList = Array.isArray(caseList) ? caseList : (caseList.data || []);
        this.faqList = Array.isArray(faqList) ? faqList : (faqList.data || []);
        this.honorList = Array.isArray(honorList) ? honorList : (honorList.data || []);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        this.loading = false;
      }
    },
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    scrollToSection(id) {
      const element = document.getElementById(id);
      if (element) {
        const offset = 80;
        const top = element.offsetTop - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
      if (this.menuOpen) {
        this.menuOpen = false;
      }
    },
    handleScroll() {
      const scrollPos = window.scrollY + 100;
      let currentId = 'home';
      for (let item of this.navItems) {
        const section = document.getElementById(item.id);
        if (section) {
          const offsetTop = section.offsetTop;
          const offsetBottom = offsetTop + section.offsetHeight;
          if (scrollPos >= offsetTop && scrollPos < offsetBottom) {
            currentId = item.id;
            break;
          }
        }
      }
      this.activeLink = currentId;
    },
    toggleFaq(index) {
      this.activeFaq = this.activeFaq === index ? null : index;
    },
    getCaseTags(tag) {
      if (!tag) return []
      return tag.split(/[,，]/).filter(t => t.trim())
    },
    getHonorType(name) {
      if (name.includes('经营') || name.includes('许可')) return '经营资质类'
      if (name.includes('信用') || name.includes('AAA')) return '荣誉评级类'
      if (name.includes('安全') || name.includes('标准化')) return '资质认证类'
      return '荣誉称号类'
    },
    formatAnswer(answer) {
      if (!answer) return ''
      return answer.replace(/\n/g, '<br>')
    },
    async handleSubmit() {
      try {
        this.formData.createdAt = new Date().toISOString()
        await api.submitForm(this.formData);
        alert(`感谢咨询，我们将尽快联系您！\n姓名：${this.formData.name}\n电话：${this.formData.phone}`);
        this.formData = { name: '', phone: '', courseType: '', message: ''};
      } catch (err) {
        alert('提交失败，请稍后重试');
      }
    }
  }
}
</script>

<style scoped>
/* 全局样式 - 与style-flag.html一致 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Microsoft YaHei", sans-serif;
}
.app-container {
  padding-top: 0;
}
.container {
  width: 1200px;
  margin: 0 auto;
}
section {
  padding: 80px 0;
}
section:nth-child(even) {
  background-color: var(--light-color);
}
.section-title {
  text-align: center;
  margin-bottom: 60px;
}
.section-title h2 {
  font-size: 36px;
  color: var(--primary-color);
  margin-bottom: 15px;
  font-weight: 700;
}
.section-title p {
  font-size: 16px;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
}
.breadcrumb {
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
}
.breadcrumb a {
  color: var(--primary-color);
}
header {
  width: 100%;
  background-color: var(--white);
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  position: sticky;
  top: 0;
  z-index: 999;
}
.header-wrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
}
.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 22px;
  font-weight: 700;
  color: var(--primary-color);
}
.sigma-tag {
  background-color: var(--primary-color);
  color: var(--white);
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
}
nav ul {
  display: flex;
  gap: 35px;
  list-style: none;
}
nav ul li a {
  font-size: 16px;
  font-weight: 500;
  color: var(--text-color);
  transition: color 0.3s;
  padding: 5px 0;
  border-bottom: 2px solid transparent;
  text-decoration: none;
}
nav ul li a:hover {
  color: var(--primary-color);
  border-bottom: 2px solid var(--primary-color);
}
nav ul li a.active {
  color: var(--primary-color);
  border-bottom: 2px solid var(--primary-color);
}
.mobile-menu {
  display: none;
  font-size: 24px;
  cursor: pointer;
}

/* Banner样式 */
.banner {
  width: 100%;
  height: 600px;
  background: linear-gradient(rgba(15,76,129,0.9), rgba(15,76,129,0.85)), url(https://img0.baidu.com/it/u=3234567890,1234567890&fm=253&fmt=auto&app=138&f=JPEG?w=1920&h=1080) center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--white);
}
.banner-content h1 {
  font-size: 42px;
  margin-bottom: 20px;
  font-weight: 700;
}
.banner-content p {
  font-size: 20px;
  margin-bottom: 35px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}
.banner-btn {
  display: flex;
  gap: 20px;
  justify-content: center;
}
.banner-btn a {
  padding: 12px 35px;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s;
  text-decoration: none;
}
.btn-primary {
  background-color: var(--secondary-color);
  color: var(--white);
}
.btn-primary:hover {
  background-color: #E56F00;
  transform: translateY(-2px);
}
.btn-outline {
  border: 2px solid var(--white);
  color: var(--white);
}
.btn-outline:hover {
  background-color: var(--white);
  color: var(--primary-color);
}

/* 企业介绍 */
.about-wrap {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  align-items: start;
}
.about-img {
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}
.about-img img {
  width: 100%;
  height: auto;
  display: block;
}
.about-content h3 {
  font-size: 28px;
  color: var(--primary-color);
  margin-bottom: 20px;
}
.about-content p {
  font-size: 16px;
  line-height: 2;
  margin-bottom: 20px;
  text-indent: 2em;
  color: #333;
}
.about-data {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-top: 30px;
  padding-top: 30px;
  border-top: 1px solid var(--border-color);
}
.data-item {
  text-align: center;
}
.data-num {
  display: block;
  font-size: 32px;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 5px;
}
.data-label {
  font-size: 14px;
  color: #666;
}

/* 服务介绍 */
.service-wrap {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}
.service-card {
  background-color: var(--white);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.08);
  transition: transform 0.3s, box-shadow 0.3s;
}
.service-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 10px 25px rgba(0,0,0,0.12);
}
.service-img {
  width: 100%;
  height: 220px;
  object-fit: cover;
}
.service-content {
  padding: 25px;
}
.service-content h3 {
  font-size: 22px;
  color: var(--primary-color);
  margin-bottom: 15px;
}
.service-tags {
  margin-bottom: 15px;
}
.service-tag {
  display: inline-block;
  padding: 4px 10px;
  background-color: var(--light-color);
  color: var(--primary-color);
  border-radius: 4px;
  font-size: 13px;
  margin-right: 8px;
  margin-bottom: 8px;
}
.service-content p {
  font-size: 15px;
  color: #666;
  line-height: 1.7;
}

/* 客户案例 */
.case-filter {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 40px;
  list-style: none;
}
.case-filter li {
  padding: 8px 20px;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s;
}
.case-filter li.active {
  background-color: var(--primary-color);
  color: var(--white);
}
.case-wrap {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}
.case-card {
  background-color: var(--white);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.08);
}
.case-img {
  width: 100%;
  height: 200px;
  object-fit: cover;
}
.case-content {
  padding: 20px;
}
.case-tags {
  margin-bottom: 10px;
}
.case-tag {
  display: inline-block;
  padding: 3px 8px;
  background-color: var(--secondary-color);
  color: var(--white);
  border-radius: 4px;
  font-size: 12px;
  margin-right: 6px;
}
.case-content h3 {
  font-size: 20px;
  color: var(--primary-color);
  margin-bottom: 10px;
}
.case-desc {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 15px;
}
.case-detail {
  font-size: 14px;
  line-height: 1.7;
  color: #333;
  border-top: 1px solid var(--border-color);
  padding-top: 15px;
}

/* 常见问题 */
.faq-wrap {
  max-width: 900px;
  margin: 0 auto;
}
.faq-item {
  margin-bottom: 15px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
}
.faq-question {
  padding: 18px 25px;
  background-color: var(--white);
  font-size: 18px;
  font-weight: 600;
  color: var(--primary-color);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.faq-question::after {
  content: "+";
  font-size: 24px;
  color: var(--secondary-color);
  transition: all 0.3s ease;
}
.faq-item.active .faq-question::after {
  transform: rotate(45deg);
}
.faq-answer {
  padding: 0 25px 18px;
  font-size: 16px;
  line-height: 1.8;
  color: #666;
}

/* 荣誉资质 */
.honor-wrap {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
}
.honor-card {
  background-color: var(--white);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.08);
  display: grid;
  grid-template-columns: 1fr 1.5fr;
}
.honor-img-wrap {
  background-color: var(--light-color);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.honor-img-wrap img {
  border: 1px solid var(--border-color);
  border-radius: 4px;
  max-height: 220px;
  width: auto;
}
.honor-card-content {
  padding: 25px;
}
.honor-card-content h3 {
  font-size: 20px;
  color: var(--primary-color);
  margin-bottom: 10px;
}
.honor-type {
  display: inline-block;
  padding: 4px 10px;
  background-color: var(--primary-color);
  color: var(--white);
  border-radius: 4px;
  font-size: 13px;
  margin-bottom: 15px;
}
.honor-card-desc {
  font-size: 14px;
  line-height: 1.7;
  margin-bottom: 10px;
  color: #666;
}
.honor-code, .honor-check {
  font-size: 14px;
  margin-bottom: 8px;
  color: #333;
}
.honor-code strong, .honor-check strong {
  color: var(--primary-color);
}

/* 联系我们 */
.contact-wrap {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
}
.contact-info h3 {
  font-size: 28px;
  color: var(--primary-color);
  margin-bottom: 30px;
}
.contact-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 25px;
}
.contact-item h4 {
  font-size: 18px;
  margin-bottom: 8px;
  color: var(--primary-color);
}
.contact-item p {
  font-size: 16px;
  line-height: 1.6;
  color: #666;
}
.contact-form {
  background-color: var(--white);
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.08);
}
.contact-form h3 {
  font-size: 24px;
  color: var(--primary-color);
  margin-bottom: 30px;
}
.form-group {
  margin-bottom: 20px;
}
.form-group label {
  display: block;
  font-size: 15px;
  margin-bottom: 8px;
  font-weight: 500;
}
.form-group input, .form-group textarea {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 15px;
}
.form-group textarea {
  resize: vertical;
  min-height: 120px;
}
.submit-btn {
  width: 100%;
  padding: 14px;
  background-color: var(--primary-color);
  color: var(--white);
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;
}
.submit-btn:hover {
  background-color: #0C3D6A;
}

/* 页脚 */
footer {
  background-color: #0A2A48;
  color: #C9D1D9;
  padding: 60px 0 20px;
}
.footer-wrap {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 40px;
  margin-bottom: 40px;
}
.footer-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  font-weight: 700;
  color: var(--white);
  margin-bottom: 20px;
}
.footer-desc {
  font-size: 14px;
  line-height: 1.8;
  margin-bottom: 20px;
}
.footer-title {
  font-size: 18px;
  color: var(--white);
  margin-bottom: 20px;
  font-weight: 600;
}
.footer-nav ul {
  list-style: none;
}
.footer-nav ul li {
  margin-bottom: 12px;
}
.footer-nav ul li a {
  font-size: 14px;
  transition: color 0.3s;
  color: #C9D1D9;
  text-decoration: none;
}
.footer-nav ul li a:hover {
  color: var(--secondary-color);
}
.footer-copyright {
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid rgba(255,255,255,0.1);
  font-size: 14px;
}
.footer-copyright .sigma-tag {
  display: inline-block;
  margin-left: 10px;
}

@media (max-width: 1200px) {
  .container {
    width: 95%;
  }
}
@media (max-width: 992px) {
  .about-wrap, .contact-wrap, .footer-wrap {
    grid-template-columns: 1fr 1fr;
  }
  .about-data {
    grid-template-columns: repeat(2, 1fr);
  }
  .service-wrap, .case-wrap {
    grid-template-columns: repeat(2, 1fr);
  }
  .honor-wrap {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 768px) {
  nav ul {
    position: fixed;
    top: 80px;
    left: -100%;
    flex-direction: column;
    background: var(--white);
    width: 100%;
    height: calc(100vh - 80px);
    padding: 40px 20px;
    gap: 30px;
    transition: all 0.3s ease;
  }
  nav ul.active {
    left: 0;
  }
  .mobile-menu {
    display: block;
  }
  .banner {
    height: 500px;
  }
  .banner-content h1 {
    font-size: 28px;
  }
  .section-title h2 {
    font-size: 28px;
  }
  .about-wrap, .contact-wrap, .footer-wrap, .case-wrap {
    grid-template-columns: 1fr;
  }
  .about-data {
    grid-template-columns: repeat(2, 1fr);
  }
  .service-wrap, .case-wrap {
    grid-template-columns: 1fr;
  }
  .case-filter {
    flex-wrap: wrap;
  }
}
</style>